import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Box,
  Button,
  Divider,
  HStack,
  VStack,
  Heading,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  useToast,
  FormControl,
  FormLabel,
  Textarea,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Link,
  Spinner,
  Stack, // Import Stack for responsive layout
} from '@chakra-ui/react';
import { db } from '../firebaseConfig';
import useOrgSettings from '../hooks/useOrgSettings';

export default function Salespeople() {
  const toast = useToast();
  const { orgId, companyName } = useOrgSettings();

  // Cloud Functions (europe-west2)
  const functions = useMemo(() => getFunctions(undefined, 'europe-west2'), []);
  const fnCreate = useMemo(() => httpsCallable(functions, 'adminCreateSalesperson'), [functions]);
  const fnUpdate = useMemo(() => httpsCallable(functions, 'adminUpdateSalesperson'), [functions]);
  const fnArchive = useMemo(() => httpsCallable(functions, 'adminArchiveSalesperson'), [functions]);
  const fnRestore = useMemo(() => httpsCallable(functions, 'adminRestoreSalesperson'), [functions]);
  const fnResetPwd = useMemo(
    () => httpsCallable(functions, 'adminResetSalespersonPassword'),
    [functions]
  );

  // Add form state
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    postcode: '',
    notes: '',
  });

  // Listing & UI state
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('active'); // active | archived
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Modals
  const [pwdModal, setPwdModal] = useState({ open: false, password: '', salesperson: null });
  const [editModal, setEditModal] = useState({
    open: false,
    uid: '',
    values: { firstName: '', lastName: '', phone: '', postcode: '', notes: '' },
  });

  // Live list
  useEffect(() => {
    if (!orgId) return;
    const colRef = collection(db, `organizations/${orgId}/salespeople`);
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('[Salespeople] listen error:', err);
        toast({ title: 'Error loading salespeople', status: 'error', isClosable: true });
        setLoading(false);
      }
    );
    return () => unsub();
  }, [orgId, toast]);

  const requiredMissing = () =>
    !form.firstName.trim() ||
    !form.lastName.trim() ||
    !form.phone.trim() ||
    !form.email.trim() ||
    !form.postcode.trim();

  // Create salesperson
  const handleAdd = async () => {
    if (!orgId) return toast({ title: 'Organization not set.', status: 'warning' });
    if (requiredMissing()) {
      toast({ title: 'Please fill all required fields.', status: 'warning', isClosable: true });
      return;
    }
    setSaving(true);
    try {
      const res = await fnCreate({
        orgId,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        postcode: form.postcode.trim(),
        notes: form.notes?.trim() || '',
      });
      const { tempPassword } = res.data || {};
      if (tempPassword) {
        setPwdModal({
          open: true,
          password: tempPassword,
          salesperson: { ...form },
        });
      }
      setForm({ firstName: '', lastName: '', phone: '', email: '', postcode: '', notes: '' });
      toast({ title: 'Salesperson added.', status: 'success', isClosable: true });
      toast({
  title: 'Important!',
  description: 'Rep must log in before any leads assigned to them. If not the lead will fail!',
  status: 'warning',
  duration: 6000,
  isClosable: true,
});
    } catch (e) {
      console.error('[Salespeople] create error:', e);
      toast({
        title: 'Error adding salesperson',
        description: e.message,
        status: 'error',
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // Open edit modal
  const openEdit = (sp) => {
    setEditModal({
      open: true,
      uid: sp.id,
      values: {
        firstName: sp.firstName || '',
        lastName: sp.lastName || '',
        phone: sp.phone || '',
        postcode: sp.postcode || '',
        notes: sp.notes || '',
      },
    });
  };

  // Save edit
  const saveEdit = async () => {
    const { uid, values } = editModal;
    if (!uid) return;
    try {
      await fnUpdate({ orgId, uid, ...values });
      toast({ title: 'Saved.', status: 'success', isClosable: true });
      setEditModal({
        open: false,
        uid: '',
        values: { firstName: '', lastName: '', phone: '', postcode: '', notes: '' },
      });
    } catch (e) {
      console.error('[Salespeople] update error:', e);
      toast({ title: 'Error saving', description: e.message, status: 'error', isClosable: true });
    }
  };

  // Archive
  const handleArchive = async (uid) => {
    if (!orgId) return;
    if (!window.confirm('Archive this salesperson? They will be disabled and moved to Archived.'))
      return;
    try {
      await fnArchive({ orgId, uid });
      toast({ title: 'Salesperson archived.', status: 'info', isClosable: true });
    } catch (e) {
      console.error('[Salespeople] archive error:', e);
      toast({
        title: 'Error archiving',
        description: e.message,
        status: 'error',
        isClosable: true,
      });
    }
  };

  // Restore
  const handleRestore = async (uid) => {
    if (!orgId) return;
    try {
      await fnRestore({ orgId, uid });
      toast({ title: 'Salesperson restored.', status: 'success', isClosable: true });
    } catch (e) {
      console.error('[Salespeople] restore error:', e);
      toast({
        title: 'Error restoring',
        description: e.message,
        status: 'error',
        isClosable: true,
      });
    }
  };

  // Reset password
  const handleResetPassword = async (uid) => {
    try {
      const res = await fnResetPwd({ orgId, uid });
      const { tempPassword } = res.data || {};
      const sp = rows.find((r) => r.id === uid);
      if (tempPassword && sp) {
        setPwdModal({
          open: true,
          password: tempPassword,
          salesperson: sp,
        });
      }
      toast({ title: 'Password reset.', status: 'success', isClosable: true });
    } catch (e) {
      console.error('[Salespeople] reset pwd error:', e);
      toast({
        title: 'Error resetting password',
        description: e.message,
        status: 'error',
        isClosable: true,
      });
    }
  };

  // Filter rows for display
  const visibleRows = rows.filter((sp) => {
    const status = (sp.status || 'active').toLowerCase();
    return filter === 'archived' ? status === 'archived' : status !== 'archived';
  });

  // --- NEW: Generate mailto link for invite ---
  const generateInviteLink = () => {
    if (!pwdModal.salesperson) return '';

    const subject = 'Your Invitation to the FixiHub Sales App';
    const body = `Hi ${pwdModal.salesperson.firstName},

You've been invited to join our team on the FixiHub mobile app. Please use the details below to log in.

Link: https://mobile.fixihub.pro
Password: ${pwdModal.password}

Thanks,
${companyName}

---
For suggestions, bugs, or improvements, please contact developers@fixihub.pro`;

    return `mailto:${pwdModal.salesperson.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <Box borderWidth={1} borderRadius="lg" p={0} mt={0} boxShadow="lg" bg="white">
      <VStack align="stretch" spacing={4} p={6}>
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Heading size="lg" color="blue.600">
            Sales Reps
          </Heading>
          <HStack>
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              width={{ base: '120px', sm: '160px', md: '200px' }}
            >
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </Select>
          </HStack>
        </HStack>

        <Text fontSize="md" color="gray.600">
          Manage your Sales Reps: add new reps, send out invites, update information, archive old
          users, or reset passwords.
        </Text>
      </VStack>

      {/* Content */}
      <VStack align="stretch" spacing={6} px={6}>
        {/* Loading state (visual consistency with AccountDetails) */}
        {loading ? (
          <HStack spacing={4}>
            <Spinner size="lg" />
            <Text fontSize="lg">Loading…</Text>
          </HStack>
        ) : (
          <>
            {/* Table mobile-friendly */}
            <Text
              fontSize="sm"
              color="gray.500"
              display={{ base: 'block', md: 'none' }}
              textAlign="left"
              mb={2}
            >
              Swipe to see more →
            </Text>
            <Box overflowX="auto">
              <Table size="sm" variant="simple" minW="900px">
                <Thead>
                  <Tr>
                    <Th>First</Th>
                    <Th>Last</Th>
                    <Th>Email</Th>
                    <Th>Phone</Th>
                    <Th>Postcode</Th>
                    <Th>Role</Th>
                    <Th>Status</Th>
                    <Th textAlign="right">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {visibleRows.map((sp) => (
                    <React.Fragment key={sp.id}>
                      <Tr>
                        <Td>{sp.firstName || '-'}</Td>
                        <Td>{sp.lastName || '-'}</Td>
                        <Td>{sp.email || '-'}</Td>
                        <Td>{sp.phone || '-'}</Td>
                        <Td>{sp.postcode || '-'}</Td>
                        <Td>
                          <Badge>{sp.role || 'sales'}</Badge>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={(sp.status || 'active') === 'archived' ? 'yellow' : 'green'}
                          >
                            {sp.status || 'active'}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack justify="flex-end" spacing={2}>
                            <Button size="sm" onClick={() => openEdit(sp)}>
                              Edit
                            </Button>
                            <Button size="sm" onClick={() => handleResetPassword(sp.id)}>
                              Reset password
                            </Button>
                            {(sp.status || 'active') === 'archived' ? (
                              <Button size="sm" onClick={() => handleRestore(sp.id)}>
                                Restore
                              </Button>
                            ) : (
                              <Button
                                colorScheme="yellow"
                                size="sm"
                                onClick={() => handleArchive(sp.id)}
                              >
                                Archive
                              </Button>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                      {/* Notes row */}
                      <Tr>
                        <Td colSpan={8}>
                          <Box bg="gray.50" p={2} borderRadius="md">
                            <Text fontSize="sm" color="gray.600">
                              <b>Notes:</b> {sp.notes?.trim() ? sp.notes : '—'}
                            </Text>
                          </Box>
                        </Td>
                      </Tr>
                    </React.Fragment>
                  ))}
                </Tbody>
              </Table>
            </Box>

            <Divider />

            {/* Add form */}
            <Box borderWidth="1px" borderRadius="lg" p={6}>
              <Text fontWeight="semibold" mb={3}>
                Add Salesperson
              </Text>
              <VStack spacing={4} align="stretch">
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      First name
                    </FormLabel>
                    <Input
                      size={{ base: 'md', md: 'lg' }}
                      placeholder="First name"
                      value={form.firstName}
                      onChange={(e) => setForm((s) => ({ ...s, firstName: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Last name
                    </FormLabel>
                    <Input
                      size={{ base: 'md', md: 'lg' }}
                      placeholder="Last name"
                      value={form.lastName}
                      onChange={(e) => setForm((s) => ({ ...s, lastName: e.target.value }))}
                    />
                  </FormControl>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Phone
                    </FormLabel>
                    <Input
                      size={{ base: 'md', md: 'lg' }}
                      placeholder="Phone"
                      value={form.phone}
                      onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Email
                    </FormLabel>
                    <Input
                      size={{ base: 'md', md: 'lg' }}
                      placeholder="Email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Postcode
                    </FormLabel>
                    <Input
                      size={{ base: 'md', md: 'lg' }}
                      placeholder="Postcode"
                      value={form.postcode}
                      onChange={(e) => setForm((s) => ({ ...s, postcode: e.target.value }))}
                    />
                  </FormControl>
                </Stack>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                    Notes
                  </FormLabel>
                  <Textarea
                    placeholder="Internal notes (optional)"
                    value={form.notes}
                    onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                    rows={2}
                  />
                </FormControl>
                <HStack justify="flex-end" pt={2}>
                  <Button colorScheme="blue" size="lg" onClick={handleAdd} isLoading={saving}>
                    Add
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </>
        )}
      </VStack>

      {/* Temp password modal */}
      <Modal
        isOpen={pwdModal.open}
        onClose={() => setPwdModal({ open: false, password: '', salesperson: null })}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Salesperson Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={2}>
              Share the password with the rep. They will use it to log in to the mobile app. Save it in the "notes" for easy access.
            </Text>
            <Input value={pwdModal.password} isReadOnly />
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(pwdModal.password);
                toast({ title: 'Copied', status: 'success', isClosable: true });
              }}
              mr={3}
            >
              Copy Password
            </Button>
            <Button as={Link} href={generateInviteLink()} colorScheme="green" mr={3}>
              Invite via Email
            </Button>
            <Button
              variant="ghost"
              onClick={() => setPwdModal({ open: false, password: '', salesperson: null })}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={editModal.open}
        onClose={() => setEditModal((s) => ({ ...s, open: false }))}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit salesperson</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <Stack direction={{ base: 'column', md: 'row' }} w="100%">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                    First name
                  </FormLabel>
                  <Input
                    size={{ base: 'md', md: 'lg' }}
                    value={editModal.values.firstName}
                    onChange={(e) =>
                      setEditModal((s) => ({
                        ...s,
                        values: { ...s.values, firstName: e.target.value },
                      }))
                    }
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                    Last name
                  </FormLabel>
                  <Input
                    size={{ base: 'md', md: 'lg' }}
                    value={editModal.values.lastName}
                    onChange={(e) =>
                      setEditModal((s) => ({
                        ...s,
                        values: { ...s.values, lastName: e.target.value },
                      }))
                    }
                  />
                </FormControl>
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} w="100%">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                    Phone
                  </FormLabel>
                  <Input
                    size={{ base: 'md', md: 'lg' }}
                    value={editModal.values.phone}
                    onChange={(e) =>
                      setEditModal((s) => ({
                        ...s,
                        values: { ...s.values, phone: e.target.value },
                      }))
                    }
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                    Postcode
                  </FormLabel>
                  <Input
                    size={{ base: 'md', md: 'lg' }}
                    value={editModal.values.postcode}
                    onChange={(e) =>
                      setEditModal((s) => ({
                        ...s,
                        values: { ...s.values, postcode: e.target.value },
                      }))
                    }
                  />
                </FormControl>
              </Stack>
              <FormControl w="100%">
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Notes
                </FormLabel>
                <Textarea
                  rows={3}
                  value={editModal.values.notes}
                  onChange={(e) =>
                    setEditModal((s) => ({ ...s, values: { ...s.values, notes: e.target.value } }))
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={saveEdit} colorScheme="blue" mr={3}>
              Save
            </Button>
            <Button variant="ghost" onClick={() => setEditModal((s) => ({ ...s, open: false }))}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}